import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowsClockwise, CheckCircle, ClockCounterClockwise, HardDrives } from '@phosphor-icons/react';
import { formatBytes } from '../../services/SandboxService';
import { formatRelativeTime } from '../../utils/relativeTime';
import { getMySandboxStats } from '../../services/MySandboxService';
import { AskPlanButton, LIMIT_ROWS, limitText, planExtras } from './PlanRequest';
import { volumeSize } from './UsageMeters';
import './MySandboxStats.css';

/**
 * The person's own sandbox in detail (the gateway's `GET /api/v1/me/sandbox/stats`, services/MySandboxService.js):
 * active hours over 30 days against the plan, both disks and what fills the data one, the release it runs, the plan
 * with "Ask for another plan", and what happened to it lately. Only ever the person's own data. Home shows it behind
 * the usage card's "Details" disclosure (UsageCard.jsx).
 */

// What fills the data volume, in the volume's order; each store keeps its colour whatever else is there.
export const STORES = [
  { key: 'postgres', label: 'Postgres' },
  { key: 'mongodb', label: 'MongoDB' },
  { key: 'redis', label: 'Redis' },
  { key: 'neo4j', label: 'Neo4j' },
  { key: 'sqlite', label: 'SQLite' },
  { key: 'kafka', label: 'Kafka' },
  { key: 's3', label: 'S3 files' },
  // after the others, so they keep their colours
  { key: 'mysql', label: 'MySQL' },
];

/** "2.5 h", "40 min", "0 h". */
export function formatHours(hours) {
  const value = Number(hours || 0);
  if (value <= 0) { return '0 h'; }
  if (value < 1) { return `${Math.max(1, Math.round(value * 60))} min`; }
  return `${value < 10 ? value.toFixed(1).replace(/\.0$/, '') : Math.round(value)} h`;
}

const shortDate = (iso) => new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });
const pct = (used, of) => (typeof used === 'number' && typeof of === 'number' && of > 0 ? Math.min(100, Math.round((used / of) * 100)) : null);

/** A round top for the axis: 1, 2, 4, 5, 8, 10, 12, 16, 20, 24 h, then tens. */
export function axisMax(max) {
  const steps = [1, 2, 4, 5, 8, 10, 12, 16, 20, 24];
  return steps.find((step) => step >= max) || Math.ceil(max / 10) * 10;
}

/** Active hours a day over the last 30 days, with this month against the plan's hours. */
export function HoursChart({ daily = [], thisMonth = null, limit = null }) {
  const [active, setActive] = useState(null);
  const days = Array.isArray(daily) ? daily : [];
  const hours = days.map((day) => Math.max(0, Number(day.activeHours || 0)));
  const top = axisMax(Math.max(0, ...hours));
  const total = hours.reduce((sum, value) => sum + value, 0);
  const share = pct(thisMonth, limit);
  return (
    <div className="mss-block">
      <div className="mss-block-head">
        <h3>Active time</h3>
        <span>
          <strong>{formatHours(thisMonth)}</strong> this month{limit ? ` of ${formatHours(limit)}` : ' · no limit'}
        </span>
      </div>
      {share !== null && (
        <span aria-label={`Active time: ${share}% of this month's hours`} className="sbx-bar" role="img">
          <span className={`sbx-bar-fill${share >= 90 ? ' warn' : ''}`} style={{ width: `${share}%` }} />
        </span>
      )}
      {days.length === 0 || total === 0 ? (
        <p className="mss-empty">No active time in the last 30 days.</p>
      ) : (
        <div className="mss-chart" onMouseLeave={() => setActive(null)}>
          <span aria-hidden="true" className="mss-chart-top">{formatHours(top)}</span>
          <div aria-label={`Active time a day, last 30 days: ${formatHours(total)} in all`} className="mss-chart-cols" role="img">
            {days.map((day, index) => (
              <div
                className={`mss-col${active === index ? ' active' : ''}`}
                data-testid="hours-col"
                key={day.date}
                onFocus={() => setActive(index)}
                onMouseEnter={() => setActive(index)}
                tabIndex={-1}
                title={`${shortDate(day.date)}: ${hours[index] > 0 ? formatHours(hours[index]) : 'no active time'}`}
              >
                <span className={`mss-bar${hours[index] > 0 ? '' : ' zero'}`} style={hours[index] > 0 ? { height: `max(3px, ${(hours[index] / top) * 100}%)` } : undefined} />
              </div>
            ))}
            {active !== null && days[active] && (
              <div className="mss-tip" role="status" style={{ left: `${((active + 0.5) / days.length) * 100}%` }}>
                <strong>{shortDate(days[active].date)}</strong>
                <span>{hours[active] > 0 ? formatHours(hours[active]) : 'no active time'}</span>
              </div>
            )}
          </div>
          <div aria-hidden="true" className="mss-chart-x">
            {days.map((day, index) => <span key={day.date}>{(days.length - 1 - index) % 7 === 0 ? shortDate(day.date) : ''}</span>)}
          </div>
        </div>
      )}
    </div>
  );
}

HoursChart.propTypes = { daily: PropTypes.array, thisMonth: PropTypes.number, limit: PropTypes.number };

function VolumeRow({ label, note, volume }) {
  if (!volume) { return null; }
  const size = volumeSize(volume);
  const share = pct(volume.used, size);
  return (
    <div className="mss-volume">
      <div className="mss-volume-head">
        <span><HardDrives aria-hidden="true" size={14} /> {label} <span className="mss-muted">· {note}</span></span>
        <span>{formatBytes(volume.used)}{size ? <span className="mss-muted"> of {formatBytes(size)}</span> : null}</span>
      </div>
      {share !== null && (
        <span aria-label={`${label}: ${share}% used`} className="sbx-bar" role="img">
          <span className={`sbx-bar-fill${share >= 90 ? ' warn' : ''}`} style={{ width: `${share}%` }} />
        </span>
      )}
    </div>
  );
}

VolumeRow.propTypes = { label: PropTypes.string.isRequired, note: PropTypes.string, volume: PropTypes.object };

/** The stores with data on the data volume, biggest first in the legend, fixed order in the bar. */
export function storesOf(stores) {
  return STORES
    .map((store, slot) => ({ ...store, slot, bytes: Number(stores?.[store.key] || 0) }))
    .filter((store) => store.bytes > 0);
}

export function DiskVolumes({ disk = null }) {
  if (!disk || (!disk.code && !disk.data)) { return null; }
  const filled = storesOf(disk.data?.stores);
  const sum = filled.reduce((total, store) => total + store.bytes, 0);
  return (
    <div className="mss-block">
      <div className="mss-block-head">
        <h3>Disk</h3>
        {disk.at ? <span>Measured {formatRelativeTime(disk.at)}</span> : <span>Measured while your sandbox is awake</span>}
      </div>
      <VolumeRow label="Code" note="projects and history" volume={disk.code} />
      <VolumeRow label="Data" note="databases and files" volume={disk.data} />
      {filled.length > 0 && (
        <>
          <span aria-label={`Data volume: ${filled.map((s) => `${s.label} ${formatBytes(s.bytes)}`).join(', ')}`} className="mss-stack" role="img">
            {filled.map((store) => (
              <span className={`mss-seg mss-c${store.slot + 1}`} key={store.key} style={{ flexGrow: store.bytes }} title={`${store.label}: ${formatBytes(store.bytes)}`} />
            ))}
          </span>
          <ul className="mss-legend">
            {[...filled].sort((a, b) => b.bytes - a.bytes).map((store) => (
              <li key={store.key}>
                <span aria-hidden="true" className={`mss-swatch mss-c${store.slot + 1}`} />
                {store.label} <span className="mss-muted">{formatBytes(store.bytes)} · {Math.round((store.bytes / sum) * 100)}%</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

DiskVolumes.propTypes = { disk: PropTypes.object };

/** The release as one pill: "Release X · up to date", or "Release X · update to Y on next wake". */
export function VersionLine({ version = null }) {
  if (!version || (!version.running && !version.current)) { return null; }
  const fresh = !version.outdated && !version.runningDiffers;
  const release = `Release ${version.running || '?'}`;
  return fresh
    ? <span className="sbx-pill sbx-pill-running mss-version"><CheckCircle aria-hidden="true" size={13} weight="fill" />{release} · up to date</span>
    : (
      <span className="sbx-pill sbx-pill-building mss-version" title={version.current ? `Flow is on ${version.current}` : undefined}>
        <ArrowsClockwise aria-hidden="true" size={13} />{release} · update to {version.current || 'the current release'} on next wake
      </span>
    );
}

VersionLine.propTypes = { version: PropTypes.object };

/** The plan's limits and what it includes, with "Ask for another plan" (a smaller one too). */
export function PlanSummary({ plan = null, planRequests = null, onAsk, children = null }) {
  const name = plan?.name || planRequests?.current?.name || null;
  const limits = plan?.limits || planRequests?.current?.limits || {};
  const full = (planRequests?.plans || []).find((p) => p.name === name);
  const extras = full ? planExtras(full) : [];
  const overridden = new Set(plan?.overridden || []);
  return (
    <div className="mss-block">
      <div className="mss-block-head">
        <h3>{name ? `${name} plan` : 'Your plan'}</h3>
        <AskPlanButton data={planRequests} onOpen={onAsk} />
      </div>
      <dl className="mss-limits">
        {LIMIT_ROWS.map((row) => (
          <div key={row.key}>
            <dt>{row.label}</dt>
            <dd>{limitText(row, limits[row.key])}{overridden.has(row.key) ? <span className="mss-muted"> · set for you</span> : null}</dd>
          </div>
        ))}
      </dl>
      {extras.length > 0 && <p className="mss-extras">Includes {extras.join(' · ')}</p>}
      {!planRequests && <p className="mss-extras">To change your plan, ask your sandbox admin.</p>}
      {children}
    </div>
  );
}

PlanSummary.propTypes = { plan: PropTypes.object, planRequests: PropTypes.object, onAsk: PropTypes.func.isRequired, children: PropTypes.node };

const LIMIT_KINDS = { runningServices: 'services running at once', disk: 'disk space' };

/** One line for an event of the person's sandbox. */
export function eventText({ type, detail = {} }) {
  switch (type) {
    case 'create': return 'Your sandbox was created';
    case 'resume': return 'Woke up';
    case 'suspend': return detail.reason === 'person' ? 'You put it to sleep' : 'Went to sleep';
    case 'limit_hit': return `Reached the plan’s limit for ${LIMIT_KINDS[detail.kind] || detail.kind || 'a resource'}`;
    case 'plan_changed': return `Plan changed${detail.from ? ` from ${detail.from}` : ''} to ${detail.to || '—'}`;
    case 'override_set': return 'Your sandbox admin changed your limits';
    case 'hours_exhausted': return 'This month’s active hours are used up';
    case 'hours_grace': return 'Started for a short grace period after the month’s hours ran out';
    case 'admin_suspend': return 'Put to sleep by your sandbox admin';
    case 'admin_wake': return 'Woken by your sandbox admin';
    case 'admin_restart': return 'Restarted by your sandbox admin';
    case 'admin_apply': return 'Restarted to apply your plan';
    case 'admin_update': return `Updated${detail.to ? ` to ${detail.to}` : ''}`;
    case 'admin_rollback': return `Rolled back${detail.to ? ` to ${detail.to}` : ''}`;
    case 'plan_request': return `Plan request for ${detail.to || 'another plan'}: ${detail.action === 'ask' ? 'sent' : detail.action || 'updated'}`;
    case 'self_restart': return 'Restarted by you';
    case 'self_clear_caches': return 'Preview caches cleared by you';
    case 'self_delete':
      if (detail.status === 'failed') { return 'Your delete stopped: the backup did not succeed, nothing was deleted'; }
      if (detail.status === 'backup') { return 'Deleted by you: backing up the disks first'; }
      return detail.keepVolume ? 'Deleted by you, disks kept' : 'Deleted by you, disks deleted';
    case 'tool_refused': return `${detail.label || 'An AI tool'} is not allowed by your organization`;
    default: return type;
  }
}

const RECENT = 5;

/** The last 5 events; "All events (N)" opens the rest of what the gateway keeps, in place. */
export function RecentEvents({ events = [], aside = null }) {
  const [all, setAll] = useState(false);
  const shown = all ? events : events.slice(0, RECENT);
  return (
    <div className="mss-block">
      <div className="mss-block-head"><h3>Recent events</h3>{aside}</div>
      {events.length === 0
        ? <p className="mss-empty">Nothing yet.</p>
        : (
          <ul className="mss-events">
            {shown.map((event, index) => (
              <li className={event.type === 'limit_hit' || event.type === 'hours_exhausted' ? 'warn' : undefined} key={`${event.at}-${index}`}>
                <ClockCounterClockwise aria-hidden="true" size={14} />
                <span>{eventText(event)}</span>
                <time className="mss-muted" dateTime={event.at}>{formatRelativeTime(event.at)}</time>
              </li>
            ))}
          </ul>
        )}
      {events.length > RECENT && (
        <button aria-expanded={all} className="sbx-link mss-events-more" onClick={() => setAll(!all)} type="button">
          {all ? 'Show the last 5' : `All events (${events.length})`}
        </button>
      )}
    </div>
  );
}

RecentEvents.propTypes = { events: PropTypes.array, aside: PropTypes.node };

/** The person's stats, loaded now and every `intervalMs` while the page is visible; null until (or where) there are none. */
export function useMySandboxStats({ intervalMs = 60000 } = {}) {
  const [stats, setStats] = useState(null);
  const load = useCallback(() => getMySandboxStats().then(setStats).catch(() => {}), []);
  useEffect(() => {
    load();
    const timer = setInterval(() => { if (document.visibilityState === 'visible') { load(); } }, intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs, load]);
  return [stats, load];
}

/**
 * The details: active time next to the disk by store, then the plan (with `planFooter` under it: the request's state,
 * what the meters count) next to the recent events and the release. Without stats (an older gateway, shared mode) the
 * plan alone, which still has its button.
 */
export default function MySandboxStats({ stats = null, plan = null, planRequests = null, onAskPlan, planFooter = null }) {
  const planBlock = <PlanSummary onAsk={onAskPlan} plan={stats?.plan || plan} planRequests={planRequests}>{planFooter}</PlanSummary>;
  if (!stats) { return <div className="mss-grid">{planBlock}</div>; }
  return (
    <div className="mss-grid">
      <HoursChart daily={stats.hours?.daily} limit={stats.hours?.limit} thisMonth={stats.hours?.thisMonth} />
      {stats.disk ? <DiskVolumes disk={stats.disk} /> : <div className="mss-block"><div className="mss-block-head"><h3>Disk</h3></div><p className="mss-empty">Measured while your sandbox is awake.</p></div>}
      {planBlock}
      <RecentEvents aside={<VersionLine version={stats.version} />} events={stats.events || []} />
    </div>
  );
}

MySandboxStats.propTypes = {
  stats: PropTypes.object,
  plan: PropTypes.object,
  planRequests: PropTypes.object,
  onAskPlan: PropTypes.func.isRequired,
  planFooter: PropTypes.node,
};
